import React, { PureComponent } from 'react';
import { bindActionCreators, compose } from 'redux';
import { Link, Redirect, Route, Switch, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  checkoutQuote,
  clearClientsList,
  clearSearch,
  createClient,
  createQuote,
  finalizeQuote,
  getClient,
  getClients,
  getClientsStats,
  getCSVUploadStatus,
  getPlans,
  getPolicies,
  getQuote,
  getQuotes,
  handoffQuote,
  search,
  updateClient,
  updatePlansFilter,
  updateQuote,
  uploadClientCSV,
} from '../../store/actions';
import debounce from 'lodash/debounce';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import includes from 'lodash/includes';
import Avatar from '../../components/Avatar';
import ClientsIndexView from '../../components/ClientsIndexView';
import ClientDetailsView from '../../components/ClientDetailsView';
import ClientsUploadingView from '../../components/ClientsUploadingView';
import Passport360EnrollmentView from '../../components/Passport360EnrollmentView';
import BOAEnrollmentView from '../../components/BOAEnrollmentView';
import Passport360PolicyDetailsView from '../../components/Passport360PolicyDetailsView';
import BOAPolicyDetailsView from '../../components/BOAPolicyDetailsView';
import ModalPortal from '../../components/ModalPortal';
import SearchResultRow from '../../components/SearchResultRow';
import { logException } from '../../services/utils';
import seo from '../../services/seo';
import logo from './chubb-logo-dark.svg';
import * as analytics from '../../services/analytics';
import './Dashboard.css';
import ModalShareLink from '../../components/ModalShareLink/ModalShareLink';

class Dashboard extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      searchQuery: '',
    };
    this.debouncedSearch = debounce(props.search, 250);
  }

  componentDidMount() {
    seo.setProps({
      title: `${this.props.carrier.name} Broker Tool`,
    });
  }

  componentWillUnmount() {
    window.$(this.modal).modal('hide');
  }

  handleNotifyError = (error = {}) => {
    logException(error);
    window
      .$('.Dashboard')
      .pgNotification({
        message: error.message || 'Sorry, something went wrong.',
        type: 'danger',
        position: 'top-left',
      })
      .show();
  };

  handleNotifySuccess(message) {
    window
      .$('.Dashboard')
      .pgNotification({
        message,
        type: 'success',
        position: 'top-left',
      })
      .show();
  }

  handleSearch = (e) => {
    const value = e.target.value;
    analytics.track('Broker Portal - Search Query Typed', {
      value: value.length,
    });
    this.setState({
      searchQuery: value,
    });
    if (!value.length) {
      this.props.clearSearch();
    } else if (value.length >= 3 && value.length <= 40) {
      this.debouncedSearch(value);
    }
  };

  handleSearchResultClick = (e) => {
    analytics.track('Broker Portal - Search Result Clicked', {
      label: e.currentTarget.dataset.itemType,
    });
    this.props.history.push(e.currentTarget.dataset.navUrl);
    this.hideSearchModal();
  };

  hideSearchModal = () => {
    analytics.track('Broker Portal - Search Modal Closed');
    window.$(this.modal).modal('hide');
    this.setState({
      searchQuery: '',
    });
    this.props.clearSearch();
  };

  showSearchModal = (e) => {
    e.preventDefault();
    analytics.track('Broker Portal - Search Modal Opened');
    window.$(this.modal).modal('show');
    this.searchField.focus();
  };

  render() {
    const {
      authToken,
      authType,
      broker,
      brokerage,
      clients,
      match,
      policies,
      quotes,
      location,
      searchResults,
    } = this.props;

    const { searchQuery } = this.state;

    if (!authToken || authType !== 'Bearer') {
      return <Redirect to="/login" />;
    } else {
      return (
        <div className="Dashboard">
          <div className="header">
            <div>
              <Link className="brand inline" to="/">
                <img src={logo} alt="Chubb logo" width="190" height="20" />
              </Link>
              <a
                href="#search"
                className="search-link hidden-md-down"
                onClick={this.showSearchModal}
              >
                <i className="far fa-search m-r-10" />
                Search...
              </a>
            </div>
            <div className="d-flex align-items-center">
              {!includes(location.pathname, 'quotes') && (
                <button
                  className="btn btn-outline-primary Dashboard-shareLink"
                  onClick={() => this.openShareLinkModal()}
                  type="button"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <span>Share Link</span>{' '}
                  <i className="fas fa-share-square"></i>
                </button>
              )}
              <div className="dropdown pull-right hidden-md-down">
                <button
                  className="profile-dropdown-toggle"
                  type="button"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <span className="semi-bold">{broker.name}</span>{' '}
                  <i className="fa fa-caret-down" />
                </button>
                <div
                  className="dropdown-menu dropdown-menu-right profile-dropdown"
                  role="menu"
                >
                  <Link to="/logout" className="dropdown-item">
                    <i className="pg-power" />
                    Logout
                  </Link>
                </div>
              </div>
              <Avatar name={broker.name} />
            </div>
          </div>

          <div className="content">
            <Switch>
              <Route
                exact
                path={`${match.path}/clients`}
                render={({ history, match }) => {
                  return (
                    <ClientsIndexView
                      {...this.props}
                      onUploadStart={() =>
                        history.push(`${match.path}/uploading`)
                      }
                      onNotifyError={this.handleNotifyError}
                      onNotifySuccess={this.handleNotifySuccess}
                    />
                  );
                }}
              />
              <Route
                exact
                path={`${match.path}/clients/uploading`}
                render={({ history }) => (
                  <ClientsUploadingView
                    {...this.props}
                    onUploadComplete={() =>
                      history.push(`${match.path}/clients`)
                    }
                    onNotifyError={this.handleNotifyError}
                    onNotifySuccess={this.handleNotifySuccess}
                  />
                )}
              />
              <Route
                exact
                path={`${match.path}/clients/:clientId`}
                render={({ history, match }) => (
                  <ClientDetailsView
                    {...this.props}
                    client={find(clients, { id: match.params.clientId })}
                    quotes={filter(quotes, {
                      client: { id: match.params.clientId },
                    })}
                    policies={filter(policies, {
                      client: { id: match.params.clientId },
                    })}
                    onNotifyError={this.handleNotifyError}
                    onNotifySuccess={this.handleNotifySuccess}
                  />
                )}
              />
              <Route
                path={`${match.path}/clients/:clientId/passport360/quotes/:quoteId`}
                render={({ match }) => (
                  <Passport360EnrollmentView
                    {...this.props}
                    actor="broker"
                    client={find(clients, { id: match.params.clientId })}
                    quote={find(quotes, {
                      quote_number: match.params.quoteId,
                    })}
                    onNotifyError={this.handleNotifyError}
                    onNotifySuccess={this.handleNotifySuccess}
                  />
                )}
              />
              <Route
                path={`${match.path}/clients/:clientId/boa/quotes/:quoteId`}
                render={({ match }) => (
                  <BOAEnrollmentView
                    {...this.props}
                    actor="broker"
                    client={find(clients, { id: match.params.clientId })}
                    quote={find(quotes, {
                      quote_number: match.params.quoteId,
                    })}
                    onNotifyError={this.handleNotifyError}
                    onNotifySuccess={this.handleNotifySuccess}
                  />
                )}
              />
              <Route
                path={`${match.path}/clients/:clientId/policies/:policyId`}
                render={({ match }) => {
                  const policy = find(policies, { id: match.params.policyId });
                  const planType = get(policy, 'plan.vertical.code');
                  switch (planType) {
                    case 'chubb_passport360':
                      return (
                        <Passport360PolicyDetailsView
                          {...this.props}
                          client={find(clients, { id: match.params.clientId })}
                          policy={find(policies, { id: match.params.policyId })}
                          onNotifyError={this.handleNotifyError}
                          onNotifySuccess={this.handleNotifySuccess}
                        />
                      );
                    case 'chubb_boa':
                      return (
                        <BOAPolicyDetailsView
                          {...this.props}
                          client={find(clients, { id: match.params.clientId })}
                          policy={find(policies, { id: match.params.policyId })}
                          onNotifyError={this.handleNotifyError}
                          onNotifySuccess={this.handleNotifySuccess}
                        />
                      );
                    default:
                      return null;
                  }
                }}
              />
              <Redirect to={`${match.path}/clients`} />
            </Switch>
          </div>

          <ModalPortal>
            <div
              className="GlobalSearchModal modal fill-in"
              id="globalSearchModal"
              ref={(c) => (this.modal = c)}
            >
              <div className="GlobalSearchModal-header header">
                <div className="brand inline">
                  <img src={logo} alt="Chubb logo" width="190" height="20" />
                </div>
                <button
                  type="button"
                  className="close"
                  aria-label="Close"
                  onClick={this.hideSearchModal}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="GlobalSearchModal-searchField">
                <div className="container-fluid">
                  <input
                    className="GlobalSearchModal-searchField-input"
                    type="text"
                    placeholder="Search..."
                    minLength="3"
                    maxLength="40"
                    value={searchQuery || ''}
                    autoFocus
                    onChange={this.handleSearch}
                    ref={(c) => (this.searchField = c)}
                  />
                  <p className="GlobalSearchModal-searchField-resultsCount">
                    {!!searchResults.length && (
                      <strong>{`${searchResults.length} `}</strong>
                    )}
                    Search results
                  </p>
                </div>
              </div>
              <div className="GlobalSearchModal-results container-fluid">
                <table className="table table-striped no-margin">
                  <tbody>
                    {searchResults.map((result) => (
                      <SearchResultRow
                        item={result}
                        onClick={this.handleSearchResultClick}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ModalPortal>
          <ModalShareLink
            brokerageSlug={brokerage.slug}
            broker={broker}
            openModal={(modal) => (this.openShareLinkModal = modal)}
          />
        </div>
      );
    }
  }
}

const mapStateToProps = (state) => {
  return {
    authToken: state.authToken,
    authType: state.authType,
    broker: state.broker,
    brokerage: state.brokerage,
    carrier: state.carrier,
    clients: state.clients,
    clientsStats: state.clientsStats,
    isCheckingOutQuote: state.isCheckingOutQuote,
    isFetchingClients: state.isFetchingClients,
    isFetchingPolicies: state.isFetchingPolicies,
    isFetchingQuotes: state.isFetchingQuotes,
    isUpdatingClient: state.isUpdatingClient,
    plans: state.plans,
    plansFilter: state.plansFilter,
    searchResults: state.searchResults,
    policies: state.policies,
    quoteCheckout: state.quoteCheckout,
    quotes: state.quotes,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      checkoutQuote,
      clearClientsList,
      clearSearch,
      createClient,
      createQuote,
      finalizeQuote,
      getClient,
      getClients,
      getClientsStats,
      getCSVUploadStatus,
      getPlans,
      getPolicies,
      getQuote,
      getQuotes,
      handoffQuote,
      search,
      updateClient,
      updatePlansFilter,
      updateQuote,
      uploadClientCSV,
    },
    dispatch
  );

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(Dashboard);
